import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { feedbackAPI } from '../services/api';
import { 
  ChatBubbleLeftRightIcon, 
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const MyFeedback = () => {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, open, replied, closed
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const response = await feedbackAPI.getMyFeedback();
      let feedbacks = response.data.feedbacks || [];
      
      // Filter by status
      if (filter !== 'all') {
        feedbacks = feedbacks.filter(f => f.status === filter);
      }
      
      setFeedbacks(feedbacks);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error('Failed to load your feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [filter]);

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: 'Open' },
      replied: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircleIcon, label: 'Replied' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircleIcon, label: 'Closed' }
    };
    const badge = badges[status] || badges.open;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/user-dashboard')}
                className="mr-4 text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 mr-3" />
                  My Feedback
                </h1>
                <p className="text-sm text-white/90 mt-1">View your sent feedback and replies</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center">
            <FunnelIcon className="w-5 h-5 text-gray-400 mr-2" />
            <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Feedback List */}
        {feedbacks.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No feedback found</p>
            <p className="text-gray-500 text-sm mt-2">
              {filter !== 'all' ? 'Try adjusting your filter' : 'You haven\'t sent any feedback yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbacks.map((feedback) => (
              <div
                key={feedback.id}
                onClick={() => setSelectedFeedback(feedback)}
                className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-indigo-600"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold text-gray-900">
                        To: {feedback.recipientType === 'manager' ? 'My Manager' : 'Super Admin'}
                      </span>
                      {feedback.recipientName && (
                        <span className="text-sm text-gray-500">
                          ({feedback.recipientName})
                        </span>
                      )}
                      {feedback.hasNewReplies && feedback.replies.length > 0 && (
                        <span className="flex h-2 w-2">
                          <span className="animate-ping absolute h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative h-2 w-2 rounded-full bg-blue-500"></span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{feedback.message}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>{formatDate(feedback.createdAt)}</span>
                      {feedback.replies.length > 0 && (
                        <>
                          <span>•</span>
                          <span className="font-medium text-blue-600">
                            {feedback.replies.length} {feedback.replies.length === 1 ? 'reply' : 'replies'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex flex-col items-end space-y-2">
                    {getStatusBadge(feedback.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Feedback Detail Modal */}
        {selectedFeedback && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-indigo-600 text-white p-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Your Feedback</h2>
                  <p className="text-sm text-indigo-100 mt-1">
                    To: {selectedFeedback.recipientType === 'manager' ? 'My Manager' : 'Super Admin'}
                    {selectedFeedback.recipientName && ` (${selectedFeedback.recipientName})`}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Your Message */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <UserIcon className="w-6 h-6 text-indigo-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">You</h3>
                        {getStatusBadge(selectedFeedback.status)}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-900 whitespace-pre-wrap">{selectedFeedback.message}</p>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        {formatDate(selectedFeedback.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Replies */}
                {selectedFeedback.replies && selectedFeedback.replies.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Replies ({selectedFeedback.replies.length})
                    </h4>
                    <div className="space-y-4">
                      {selectedFeedback.replies.map((reply) => (
                        <div key={reply.id} className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold text-gray-900">{reply.replierName || 'Manager/Admin'}</span>
                            </div>
                            <span className="text-xs text-gray-500">{formatDate(reply.createdAt)}</span>
                          </div>
                          <p className="text-gray-900 whitespace-pre-wrap">{reply.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No replies yet</p>
                    <p className="text-sm mt-1">Your {selectedFeedback.recipientType === 'manager' ? 'manager' : 'super admin'} will respond here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyFeedback;

